import { ICONS } from 'consts/consts';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'reactstrap';
import { Person } from 'types';
import { useConfirmApi } from 'utils/api/ApiHooks';
import { getPrettyDate, sanitizeUrlQueryParams } from 'utils/util/util';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import Page from 'views/Layout/Pages/Page';
import { useInfiniteScroll } from 'views/Objectives/ObjectivesOverviewPage/hooks';
import EmptyState from 'views/Widgets/EmptyState';
import { ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import { FORMAT_AVATAR_GROUP } from 'views/Widgets/People/Filters/common';
import { useAIAgentsHub } from '../AiAgentsHubContext';
import CreateDocumentModal from './CreateDocumentModal';
import DeleteDocumentModal from './DeleteDocumentModal';
import EditDocumentModal from './EditDocumentModal';
import MediaHistoryDocumentModal from './MediaHistoryDocumentModal';
import MoreActionsButton from './MoreActionsButton';

export interface Document {
  id: number;
  name: string;
  description: string;
  visibility: 'I' | 'P';
  media: {
    id: number;
    file: string;
    filename: string;
  };
  media_history?: Array<{
    id: number;
    file: string;
    created_at: string;
  }> | null;
  owners: Person[];
  created_by_person: {
    full_name: string;
    email: string;
  };
  viewers?: ApiGroup;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  editable: boolean;
}

const DocumentsAdminPage: FC = () => {
  const { setAction } = useAIAgentsHub();
  const { formatMessage, locale } = useIntl();
  const [currentUrl, setCurrentUrl] = useState<string>('documents');
  const [, setCurrentTotalCount] = useState<number | undefined>(undefined);
  const [nextUrl, setNextUrl] = useState<string | undefined>(undefined);
  const [accumulatedData, setAccumulatedData] = useState<any[] | undefined>(
    undefined
  );
  const [isLoadingMoreItems, setIsLoadingMoreItems] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<
    number | undefined
  >(undefined);

  const callback = useCallback(
    (
      data: { results: any[]; next?: string; count: number } | null,
      error,
      hardError
    ): void => {
      if (!data || error || hardError) {
        console.error('Error fetching feedback data:', error || hardError);
        return;
      }
      setAccumulatedData((prev) => [...(prev ?? []), ...data.results]);
      setNextUrl(
        data.next ? sanitizeUrlQueryParams(data.next, ['page']) : undefined
      );
      setCurrentTotalCount(data.count);
      setIsLoadingMoreItems(false);
    },
    []
  );
  const { error, status } = useConfirmApi<{
    results: any[];
    next?: string;
    count: number;
  }>({
    method: 'GET',
    url: currentUrl,
    callback: callback,
  });

  const handleCreateClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleEditClick = useCallback((documentId: number) => {
    setSelectedDocumentId(documentId);
    setIsEditModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((documentId: number) => {
    setSelectedDocumentId(documentId);
    setIsDeleteModalOpen(true);
  }, []);

  const handleShowHistoryClick = useCallback((documentId: number) => {
    setSelectedDocumentId(documentId);
    setIsHistoryModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedDocumentId(undefined);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedDocumentId(undefined);
  }, []);

  const handleCloseHistoryModal = useCallback(() => {
    setIsHistoryModalOpen(false);
    setSelectedDocumentId(undefined);
  }, []);

  const handleCallbackCreateModal = useCallback(
    (createdDocument) => {
      handleCloseCreateModal();
      setAccumulatedData((ad) =>
        ad ? [createdDocument, ...ad] : [createdDocument]
      );
    },
    [handleCloseCreateModal]
  );

  const handleCallbackEditModal = useCallback(
    (editedDocument) => {
      handleCloseEditModal();
      setAccumulatedData((ad) =>
        ad?.map((document) =>
          document.id === editedDocument.id ? editedDocument : document
        )
      );
    },
    [handleCloseEditModal]
  );

  const handleCallbackDeleteModal = useCallback(
    (deletedDocument) => {
      handleCloseDeleteModal();
      setAccumulatedData((ad) =>
        ad?.filter((document) => document.id !== deletedDocument.id)
      );
    },
    [handleCloseDeleteModal]
  );

  const loadMoreRef = useRef(null);

  const handleLoadMore = useCallback((nextUrl) => {
    setCurrentUrl(nextUrl);
    setIsLoadingMoreItems(true);
  }, []);

  useInfiniteScroll(
    loadMoreRef,
    useCallback(() => {
      if (nextUrl) {
        handleLoadMore(nextUrl);
      }
    }, [nextUrl, handleLoadMore])
  );

  const columns = useMemo(() => {
    return [
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.name',
          defaultMessage: 'Name',
        }),
        field: 'name',
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.description',
          defaultMessage: 'Description',
        }),
        field: 'description',
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.owners',
          defaultMessage: 'Owners',
        }),
        field: 'owners',
        className: 'col-1',
        sortable: false,
        format: FORMAT_AVATAR_GROUP,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.visibility',
          defaultMessage: 'Visibility',
        }),
        field: 'visibility',
        className: 'col-1',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.updated_at',
          defaultMessage: 'Last Updated',
        }),
        field: 'updated_at',
        format: (date: string) => {
          return getPrettyDate({
            dateString: date,
            includeTime: true,
            locale,
          });
        },
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.table.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-1',
        sortable: false,
      },
    ];
  }, [formatMessage, locale]);

  const rows = useMemo(() => {
    if (!accumulatedData) {
      return [];
    }
    return accumulatedData.map((document) => {
      return {
        id: document.id,
        name: document.name,
        description: document.description || (
          <em className="text-muted">
            <FormattedMessage
              id="app.views.administration.content_administration.documents_admin.no_description"
              defaultMessage="No description"
            />
          </em>
        ),
        visibility: (
          <>
            <i
              className={
                document.visibility === 'P'
                  ? ICONS.PUBLIC
                  : document.visibility === 'I'
                    ? ICONS.PRIVATE
                    : ICONS.FILTER
              }
            />{' '}
            {document.visibility === 'P' ? (
              <FormattedMessage
                id="app.views.administration.content_administration.documents_admin.visibility.public"
                defaultMessage="Public"
              />
            ) : document.visibility === 'I' ? (
              <FormattedMessage
                id="app.views.administration.content_administration.documents_admin.visibility.private"
                defaultMessage="Private"
              />
            ) : (
              <FormattedMessage
                id="app.views.administration.content_administration.documents_admin.visibility.custom"
                defaultMessage="Custom"
              />
            )}
          </>
        ),
        viewers: document.viewers ? (
          <span className="badge bg-info text-white">
            <FormattedMessage
              id="app.views.administration.content_administration.documents_admin.viewers.filtered"
              defaultMessage="Filtered"
            />
          </span>
        ) : (
          <span className="text-muted">
            <FormattedMessage
              id="app.views.administration.content_administration.documents_admin.viewers.none"
              defaultMessage="None"
            />
          </span>
        ),
        created_at: document.created_at,
        updated_at: document.updated_at,
        owners: document.owners,
        actions: (
          <MoreActionsButton
            document={document}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            onShowHistoryClick={handleShowHistoryClick}
          />
        ),
      };
    });
  }, [
    accumulatedData,
    handleEditClick,
    handleDeleteClick,
    handleShowHistoryClick,
  ]);

  const createNewDocumentButton = useMemo(
    () => (
      <Button color="primary" onClick={handleCreateClick}>
        <FormattedMessage
          id="app.views.administration.content_administration.documents_admin.create_button"
          defaultMessage="New Document"
        />
      </Button>
    ),
    [handleCreateClick]
  );

  useEffect(() => {
    setAction(createNewDocumentButton);
  }, [createNewDocumentButton, setAction]);

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.loading',
          defaultMessage: 'Loading documents...',
        })}
      />
    );
  }

  if (error) {
    return <SimpleErrorPage error={error} />;
  }

  return (
    <Page
      embedded={true}
      title={formatMessage({
        id: 'app.views.administration.content_administration.documents_admin.title.documents',
        defaultMessage: 'Documents',
      })}
    >
      {rows.length === 0 && (
        <EmptyState
          title={formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.no_documents.title',
            defaultMessage:
              'You do not have any documents that you are eligible to view.',
          })}
          subtitle={formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.description',
            defaultMessage:
              'Manage documents for your organization. Documents can be private (only visible to the owners), public (visible to all organization members), or custom (you can set specific viewers who can access it).',
          })}
        >
          {createNewDocumentButton}
        </EmptyState>
      )}
      {rows.length > 0 && (
        <>
          <FilterablePeopleTable
            columns={columns}
            rows={rows}
            hideFilters={true}
            hideExportButton={true}
          />
          <div ref={loadMoreRef} />
          {isLoadingMoreItems && <Loading />}

          {selectedDocumentId && isEditModalOpen && (
            <EditDocumentModal
              isOpen={isEditModalOpen}
              toggle={handleCloseEditModal}
              documentId={selectedDocumentId}
              onSuccess={handleCallbackEditModal}
            />
          )}

          {selectedDocumentId && isDeleteModalOpen && (
            <DeleteDocumentModal
              isOpen={isDeleteModalOpen}
              onClosed={handleCloseDeleteModal}
              documentId={selectedDocumentId}
              onDelete={handleCallbackDeleteModal}
            />
          )}

          {selectedDocumentId && isHistoryModalOpen && (
            <MediaHistoryDocumentModal
              isOpen={isHistoryModalOpen}
              toggle={handleCloseHistoryModal}
              documentId={selectedDocumentId}
            />
          )}
        </>
      )}
      {isCreateModalOpen && (
        <CreateDocumentModal
          isOpen={isCreateModalOpen}
          toggle={handleCloseCreateModal}
          onSuccess={handleCallbackCreateModal}
        />
      )}
    </Page>
  );
};

export default React.memo(DocumentsAdminPage);
